import { FIM_ENTRY_ASSET_INSTANCE, useFimService } from 'features/fim'
import {
  type AssetInstanceSummary,
  buildAssetInventory,
} from 'features/fim/assetInventory'
import { useRolodexEntries } from 'features/rolodex'
import { useEffect, useMemo, useRef } from 'react'
import type { RolodexLoadedEntryJson } from 'types'
import { RolodexEntryType } from 'types'

/** Poll cadence while a fill is queued/running, so lifecycle rows advance. */
const ACTIVE_FILL_REFRESH_MS = 5000

const hasActiveFill = (loaded: RolodexLoadedEntryJson[][]): boolean =>
  loaded.some((batch) =>
    batch.some(({ entry }) => {
      const data = entry?.data as { fill_status?: unknown } | undefined
      return data?.fill_status === 'queued' || data?.fill_status === 'running'
    })
  )

/**
 * The Assets inventory: ONE query — `fim-asset-instance` by site. While any
 * instance's fill is queued/running the query polls so the rows advance to
 * their terminal state without a manual refresh.
 */
export const useAssetInstances = (
  siteId: string | null
): {
  assets: AssetInstanceSummary[]
  isLoading: boolean
  forceRefresh: () => Promise<void>
} => {
  const query = siteId
    ? {
        type: FIM_ENTRY_ASSET_INSTANCE as RolodexEntryType,
        tags: { site: siteId },
      }
    : undefined
  const { entries, isLoading, forceRefresh } = useRolodexEntries(query, {
    swrOptions: {
      refreshInterval: (data) =>
        data && hasActiveFill(data) ? ACTIVE_FILL_REFRESH_MS : 0,
    },
  })

  // Staleness surfacing (fill-ownership protocol v2 §10): on mount, fire the
  // RECONCILING read (`GET /sites/{site}/instances`) fire-and-forget so
  // fim-service marks instances whose subject/template moved on as `stale`,
  // then refresh the Rolodex query — which stays the load path — so the
  // freshly-marked rows appear. Best-effort: a reconcile failure never blocks
  // the plain Rolodex read. Latest callbacks live in a ref so the effect runs
  // once per site, not on callback identity churn.
  const { listInstances } = useFimService()
  const reconcile = useRef({ listInstances, forceRefresh })
  reconcile.current = { listInstances, forceRefresh }
  useEffect(() => {
    if (!siteId) return
    let cancelled = false
    reconcile.current
      .listInstances(siteId)
      .then(() => {
        if (!cancelled) void reconcile.current.forceRefresh()
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [siteId])

  const assets = useMemo(() => buildAssetInventory(entries), [entries])

  return { assets, isLoading, forceRefresh }
}

/** Resolves one selected instance row (already loaded by the index query). */
export const useAssetInstance = (
  assets: AssetInstanceSummary[],
  assetId: string | undefined
): AssetInstanceSummary | undefined =>
  useMemo(
    () => assets.find((candidate) => candidate.slug === assetId),
    [assets, assetId]
  )
